import color from '@haaretz/l-color.macro';
import fork from '@haaretz/l-fork.macro';
import merge from '@haaretz/l-merge.macro';
import mq from '@haaretz/l-mq.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import zIndex from '@haaretz/l-z-index.macro';
import * as React from 'react';
import s9 from 'style9';

import type { StyleExtend, InlineStyles } from '@haaretz/s-types';

// `c` is short for `classNames`
const c = s9.create({
  gradientWrapper: {
    display: 'flex',
    justifyContent: 'center',
    '--dflt-grdnt-strt': color('primary800'),
    '--dflt-grdnt-end': color('primary500'),
    backgroundImage:
      'linear-gradient(165.44deg, var(--dflt-grdnt-strt) 9.5%, var(--dflt-grdnt-end) 81.9%)' as string,
    marginTop: space(-26),
    marginBottom: space(-120),
    paddingTop: space(30),
    zIndex: zIndex('below'),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(-85),
        },
      })
    ),
  },
  lightGradientWrapper: {
    '--dflt-grdnt-strt': fork({
      default: color('primary500'),
      tm: color('notForQuoteBg'),
      hdc: color('primary600'),
    }),
    '--dflt-grdnt-end': color('b2bGradientEnd'),

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(-132),
        },
      })
    ),
  },
  subTitle: {},
  innerWrapper: {
    backgroundColor: color('contentBg'),
    marginRight: 'auto',
    marginLeft: 'auto',
    paddingTop: space(8),
    paddingBottom: space(8),
    paddingRight: space(3),
    paddingLeft: space(3),
    borderRadius: radius('xLarge'),
    marginBottom: fork({ default: space(21), hdc: space(25) }),
    maxWidth: `calc(100vw - ${space(6)})`,

    ...merge(
      mq({
        from: 's',
        value: {
          maxWidth: '480px',
        },
      }),
      mq({
        from: 'l',
        value: {
          paddingTop: space(15),
          paddingBottom: space(25),
          paddingRight: space(31),
          paddingLeft: space(31),
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          marginBottom: 0,
          maxWidth: '972px',
        },
      })
    ),
  },
});

export interface PpLandingPageLayoutProps {
  /** The Children to be rendered inside `<PpLandingPageLayout>` */
  children?: React.ReactNode;
  /**
   * CSS declarations to be set as inline `style` on the
   * html element.
   *
   * By setting values of CSS Custom Properties based on
   * props or state in the consuming component (where
   * the value of `inlineStyle` is passed), `inlineStyle`
   * can be used as an API contract for setting dynamic
   * values to styles created with `style9.create()`:
   *
   * @example
   * ```ts
   * import s9 from 'style9';
   * const { styleExtend, } = s9.create({
   *   styleExtend: {
   *     color: 'var(--color-based-on-prop)',
   *   },
   * });
   *
   * function MyPpLandingPageLayout(props) {
   *   const inlineStyle = {
   *     '--color-based-on-prop': props.color,
   *   },
   *
   *   return (
   *    <PpLandingPageLayout
   *      styleExtend={[ styleExtend, ]}
   *      inlineStyle={inlineStyle}
   *    />
   *   );
   * }
   * ```
   */
  inlineStyle?: InlineStyles;
  /**
   * An array of `Style`s created by `style9.create()`.
   * WARNING: **_do not_** pass simple CSS-in-JS object.
   * The items in the array must be created with Style9's
   * `create` function.
   * The array can also hold falsy values to assist with
   * conditional inclusion of `Style`s:
   *
   * @example
   * ```ts
   * const { foo, bar, } = s9.create({ foo: { ... }, bar: { ... }, });
   * <PpLandingPageLayout styleExtend={[ someCondition && foo, bar, ]} />
   * ```
   */
  styleExtend?: StyleExtend;
  titleStyleExtend?: StyleExtend;
  subTitleStyleExtend?: StyleExtend;
  innerWrapperStyleExtend?: StyleExtend;
  innerTitleStyleExtend?: StyleExtend;
  title: string;
  subTitle?: string;
  innerTitle: string;
  /** The kind of gradient to be applied to the background */
  gradientKind?: 'default' | 'light';
}

export default function PpLandingPageLayout({
  children = null,
  inlineStyle,
  styleExtend = [],
  titleStyleExtend = [],
  subTitleStyleExtend = [],
  innerTitleStyleExtend = [],
  innerWrapperStyleExtend = [],
  title,
  subTitle,
  innerTitle,
  gradientKind = 'default',
}: PpLandingPageLayoutProps) {
  return (
    <>
      <div className={s9(c.gradientWrapper, gradientKind === 'light' && c.lightGradientWrapper)} />
      <div className={s9(...styleExtend)} style={inlineStyle}>
        <h1 className={s9(...titleStyleExtend)}>{title}</h1>
        {subTitle && <h2 className={s9(c.subTitle, ...subTitleStyleExtend)}>{subTitle}</h2>}
        <div className={s9(c.innerWrapper, ...innerWrapperStyleExtend)}>
          <h3 className={s9(...innerTitleStyleExtend)}>{innerTitle}</h3>
          {children}
        </div>
      </div>
    </>
  );
}
